/**
 * @fileoverview State + actions for the Teacher Assessment Drawer (AR-539).
 *
 * Owns:
 *   - fetch lifecycle of the drawer payload (composer service)
 *   - student selection (multi + "All students")
 *   - optimistic score updates (individual + bulk + toggle enable)
 *   - destructive-disable confirmations (item-wide and per-student)
 *   - bulk overwrite confirmation derived from local state
 *
 * Notes:
 *   - State is immutable: every update produces a new `data` object via
 *     map/spread. No in-place mutation.
 *   - Saving paths handle the sad case explicitly with rollback + toast.
 *   - `t` from useTranslation is captured via a ref to avoid stale-closure
 *     warnings without putting it in callback deps (see
 *     `feedback_useArcTranslation_deps`).
 *   - The hook is intentionally agnostic of UI (no Mantine imports).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconX } from '@tabler/icons-react'
import { notifications } from 'context'
import {
  fetchTeacherAssessmentDrawerData,
  updateAssessmentScoreBulk,
  updateAssessmentScoreIndividual,
} from './service'
import { ALL_STUDENTS_VALUE } from './constants'
import type { AssessmentItem, AssessmentScore, DrawerStudent, TeacherAssessmentDrawerData } from './types'

export interface OverwritePrompt {
  itemKey: string
  masterScore: number
  affectedStudentIds: string[]
}

export type DisablePrompt =
  | { kind: 'item'; itemKey: string; affectedStudentIds: string[] }
  | { kind: 'student'; itemKey: string; studentId: string; studentName: string }

interface UseTeacherAssessmentDrawerParams {
  opened: boolean
  sessionMeetingId: string
  initialStudentId?: string | null
  /** When true, defaults the selection to "All students" (Project View). */
  defaultAllStudents?: boolean
}

interface UseTeacherAssessmentDrawerReturn {
  loading: boolean
  data: TeacherAssessmentDrawerData | null
  selectedStudentIds: string[]
  /** Students filtered by the current selection. */
  visibleStudents: DrawerStudent[]
  /** Convenience for the picker — includes "__all_students__" + students. */
  studentOptions: Array<{ value: string; label: string }>
  setStudentSelection: (next: string[]) => void

  /** Bulk overwrite confirmation; null when no prompt is active. */
  overwritePrompt: OverwritePrompt | null
  dismissOverwritePrompt: () => void
  confirmOverwritePrompt: () => void

  /** Destructive-disable confirmation for item-wide or per-student toggle. */
  disablePrompt: DisablePrompt | null
  dismissDisablePrompt: () => void
  confirmDisablePrompt: () => void

  /** Per-item score actions. */
  setStudentScore: (itemKey: string, studentId: string, masterScore: number) => Promise<void>
  applyBulkScore: (itemKey: string, masterScore: number) => Promise<void>
  toggleItemEnabled: (itemKey: string, enabled: boolean) => Promise<void>
  toggleStudentEnabled: (itemKey: string, studentId: string, enabled: boolean) => Promise<void>
}

function buildKey(item: AssessmentItem): string {
  return `${item.activityId}:${item.lv4ItemId}`
}

const ZEROED_SCORE_PATCH = { masterScore: 0, justification: '', doNotAssess: true } as const

/** True if the student has a tutor score worth confirming the destruction of. */
function hasFilledScore(score: AssessmentScore): boolean {
  return !score.doNotAssess && score.masterScore > 0
}

export function useTeacherAssessmentDrawer({
  opened,
  sessionMeetingId,
  initialStudentId,
  defaultAllStudents = false,
}: UseTeacherAssessmentDrawerParams): UseTeacherAssessmentDrawerReturn {
  const { t } = useTranslation()
  const tRef = useRef(t)
  tRef.current = t

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<TeacherAssessmentDrawerData | null>(null)
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([ALL_STUDENTS_VALUE])
  const [overwritePrompt, setOverwritePrompt] = useState<OverwritePrompt | null>(null)
  const [disablePrompt, setDisablePrompt] = useState<DisablePrompt | null>(null)

  // Load on open.
  useEffect(() => {
    if (!opened || !sessionMeetingId) return
    let cancelled = false
    setLoading(true)
    setData(null)
    fetchTeacherAssessmentDrawerData({ sessionMeetingId })
      .then(payload => {
        if (cancelled) return
        setData(payload)
        if (initialStudentId && payload.students.some(s => s.id === initialStudentId)) {
          setSelectedStudentIds([initialStudentId])
        } else {
          // defaultAllStudents OR falling back to "All students" when no initial.
          setSelectedStudentIds([ALL_STUDENTS_VALUE])
        }
      })
      .catch(() => {
        if (cancelled) return
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.load_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
        setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [opened, sessionMeetingId, initialStudentId, defaultAllStudents])

  // Reset on close.
  useEffect(() => {
    if (opened) return
    setData(null)
    setLoading(false)
    setOverwritePrompt(null)
    setDisablePrompt(null)
    setSelectedStudentIds([ALL_STUDENTS_VALUE])
  }, [opened])

  // Student selection — exclusive between "All students" and individuals.
  const setStudentSelection = useCallback((next: string[]) => {
    if (next.length === 0) {
      setSelectedStudentIds([ALL_STUDENTS_VALUE])
      return
    }
    setSelectedStudentIds(prev => {
      const includesAll = next.includes(ALL_STUDENTS_VALUE)
      if (!includesAll) return next
      const hadAll = prev.includes(ALL_STUDENTS_VALUE)
      if (hadAll) {
        const individuals = next.filter(v => v !== ALL_STUDENTS_VALUE)
        return individuals.length > 0 ? individuals : [ALL_STUDENTS_VALUE]
      }
      return [ALL_STUDENTS_VALUE]
    })
  }, [])

  const visibleStudents = useMemo<DrawerStudent[]>(() => {
    if (!data) return []
    if (selectedStudentIds.includes(ALL_STUDENTS_VALUE) || selectedStudentIds.length === 0) {
      return data.students
    }
    const set = new Set(selectedStudentIds)
    return data.students.filter(s => set.has(s.id))
  }, [data, selectedStudentIds])

  const studentOptions = useMemo(() => {
    const all = [{ value: ALL_STUDENTS_VALUE, label: tRef.current('arc_teacher_assessment_drawer.all_students') }]
    if (!data) return all
    return [
      ...all,
      ...data.students.map(s => ({ value: s.id, label: s.fullName })),
    ]
  }, [data])

  /** Produces a new `data` with one per-student score patched. Immutable. */
  const updateLocalScore = useCallback(
    (
      itemKey: string,
      studentId: string,
      patch: Partial<{ masterScore: number; doNotAssess: boolean; justification: string | null }>,
    ) => {
      setData(prev => {
        if (!prev) return prev
        const apply = (items: AssessmentItem[]): AssessmentItem[] =>
          items.map(item => {
            if (buildKey(item) !== itemKey) return item
            const ps = item.perStudent[studentId]
            if (!ps) return item
            const nextScore: AssessmentScore = {
              ...ps.score,
              ...(patch.masterScore !== undefined ? { masterScore: patch.masterScore } : {}),
              ...(patch.doNotAssess !== undefined ? { doNotAssess: patch.doNotAssess } : {}),
              ...(patch.justification !== undefined ? { justification: patch.justification } : {}),
            }
            nextScore.pendingTutorAssessment = !nextScore.doNotAssess && nextScore.masterScore === 0
            const nextItem: AssessmentItem = {
              ...item,
              perStudent: {
                ...item.perStudent,
                [studentId]: { ...ps, score: nextScore },
              },
            }
            // Item is considered disabled when every student has `doNotAssess`.
            const psValues = Object.values(nextItem.perStudent)
            nextItem.enabled =
              psValues.length === 0 || !psValues.every(p => p.score.doNotAssess)
            return nextItem
          })
        return { ...prev, standards: apply(prev.standards), skills: apply(prev.skills) }
      })
    },
    [],
  )

  const findItem = useCallback(
    (itemKey: string): AssessmentItem | undefined => {
      if (!data) return undefined
      return [...data.standards, ...data.skills].find(i => buildKey(i) === itemKey)
    },
    [data],
  )

  // ─── Per-student score ─────────────────────────────────────────────────────
  const setStudentScore = useCallback(
    async (itemKey: string, studentId: string, masterScore: number) => {
      const item = findItem(itemKey)
      if (!item) return
      const current = item.perStudent[studentId]
      if (!current) return
      // Idempotent: skip if score already matches (no network call, no toast).
      if (current.score.masterScore === masterScore && !current.score.doNotAssess) return
      const previousScore = current.score.masterScore
      updateLocalScore(itemKey, studentId, { masterScore })
      try {
        await updateAssessmentScoreIndividual({
          sessionMeetingId,
          assessmentScoreId: current.score.id,
          masterScore,
        })
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.toast.saved'),
          color: 'green',
        })
      } catch {
        updateLocalScore(itemKey, studentId, { masterScore: previousScore })
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.save_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, sessionMeetingId, updateLocalScore],
  )

  // ─── Bulk apply ────────────────────────────────────────────────────────────
  const performBulk = useCallback(
    async (itemKey: string, masterScore: number) => {
      const item = findItem(itemKey)
      if (!item) return
      // Skip students with `do_not_assess: true` — bulk does not re-enable.
      const targetIds = visibleStudents
        .map(s => s.id)
        .filter(sid => {
          const ps = item.perStudent[sid]
          return ps !== undefined && !ps.score.doNotAssess
        })
      if (targetIds.length === 0) return
      // Idempotent: skip if every target already has this score.
      const allAlreadyAtScore = targetIds.every(
        sid => item.perStudent[sid].score.masterScore === masterScore,
      )
      if (allAlreadyAtScore) return

      const snapshot = new Map<string, number>()
      for (const sid of targetIds) snapshot.set(sid, item.perStudent[sid].score.masterScore)

      for (const sid of targetIds) updateLocalScore(itemKey, sid, { masterScore })

      try {
        const usingAll = selectedStudentIds.includes(ALL_STUDENTS_VALUE)
        await updateAssessmentScoreBulk({
          sessionMeetingId,
          lv4ItemId: item.lv4ItemId,
          studentIds: usingAll ? targetIds : targetIds, // explicit even on All, to skip disabled
          masterScore,
        })
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.toast.saved'),
          color: 'green',
        })
      } catch {
        for (const [sid, prevScore] of snapshot.entries()) {
          updateLocalScore(itemKey, sid, { masterScore: prevScore })
        }
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.save_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, selectedStudentIds, sessionMeetingId, updateLocalScore, visibleStudents],
  )

  const applyBulkScore = useCallback(
    async (itemKey: string, masterScore: number) => {
      const item = findItem(itemKey)
      if (!item) return
      // Affected = visible & enabled & has a different score already set.
      const affected = visibleStudents
        .map(s => s.id)
        .filter(sid => {
          const ps = item.perStudent[sid]
          if (!ps || ps.score.doNotAssess) return false
          return ps.score.masterScore !== 0 && ps.score.masterScore !== masterScore
        })
      if (affected.length > 0) {
        setOverwritePrompt({ itemKey, masterScore, affectedStudentIds: affected })
        return
      }
      await performBulk(itemKey, masterScore)
    },
    [findItem, performBulk, visibleStudents],
  )

  const dismissOverwritePrompt = useCallback(() => setOverwritePrompt(null), [])

  const confirmOverwritePrompt = useCallback(() => {
    const prompt = overwritePrompt
    if (!prompt) return
    setOverwritePrompt(null)
    void performBulk(prompt.itemKey, prompt.masterScore)
  }, [overwritePrompt, performBulk])

  // ─── Destructive disable (item-wide) ───────────────────────────────────────
  const performToggleItemDisabled = useCallback(
    async (itemKey: string) => {
      const item = findItem(itemKey)
      if (!item) return
      const snapshot = new Map<string, AssessmentScore>()
      for (const [sid, ps] of Object.entries(item.perStudent)) {
        snapshot.set(sid, ps.score)
      }
      for (const sid of Object.keys(item.perStudent)) {
        updateLocalScore(itemKey, sid, ZEROED_SCORE_PATCH)
      }
      try {
        await updateAssessmentScoreBulk({
          sessionMeetingId,
          lv4ItemId: item.lv4ItemId,
          ...ZEROED_SCORE_PATCH,
        })
      } catch {
        for (const [sid, prev] of snapshot.entries()) {
          updateLocalScore(itemKey, sid, {
            masterScore: prev.masterScore,
            justification: prev.justification,
            doNotAssess: prev.doNotAssess,
          })
        }
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.toggle_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, sessionMeetingId, updateLocalScore],
  )

  const performToggleItemEnabledOn = useCallback(
    async (itemKey: string) => {
      const item = findItem(itemKey)
      if (!item) return
      const snapshot = new Map<string, AssessmentScore>()
      for (const [sid, ps] of Object.entries(item.perStudent)) {
        snapshot.set(sid, ps.score)
      }
      for (const sid of Object.keys(item.perStudent)) {
        updateLocalScore(itemKey, sid, { doNotAssess: false })
      }
      try {
        await updateAssessmentScoreBulk({
          sessionMeetingId,
          lv4ItemId: item.lv4ItemId,
          doNotAssess: false,
        })
      } catch {
        for (const [sid, prev] of snapshot.entries()) {
          updateLocalScore(itemKey, sid, { doNotAssess: prev.doNotAssess })
        }
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.toggle_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, sessionMeetingId, updateLocalScore],
  )

  const toggleItemEnabled = useCallback(
    async (itemKey: string, enabled: boolean) => {
      const item = findItem(itemKey)
      if (!item) return
      if (enabled) {
        await performToggleItemEnabledOn(itemKey)
        return
      }
      const studentsWithScore = Object.entries(item.perStudent)
        .filter(([, ps]) => hasFilledScore(ps.score))
        .map(([sid]) => sid)
      if (studentsWithScore.length > 0) {
        setDisablePrompt({ kind: 'item', itemKey, affectedStudentIds: studentsWithScore })
        return
      }
      await performToggleItemDisabled(itemKey)
    },
    [findItem, performToggleItemDisabled, performToggleItemEnabledOn],
  )

  // ─── Destructive disable (single student) ──────────────────────────────────
  const performToggleStudentDisabled = useCallback(
    async (itemKey: string, studentId: string) => {
      const item = findItem(itemKey)
      if (!item) return
      const current = item.perStudent[studentId]
      if (!current) return
      const previous = current.score
      updateLocalScore(itemKey, studentId, ZEROED_SCORE_PATCH)
      try {
        await updateAssessmentScoreIndividual({
          sessionMeetingId,
          assessmentScoreId: previous.id,
          ...ZEROED_SCORE_PATCH,
        })
      } catch {
        updateLocalScore(itemKey, studentId, {
          masterScore: previous.masterScore,
          justification: previous.justification,
          doNotAssess: previous.doNotAssess,
        })
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.toggle_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, sessionMeetingId, updateLocalScore],
  )

  const performToggleStudentEnabledOn = useCallback(
    async (itemKey: string, studentId: string) => {
      const item = findItem(itemKey)
      if (!item) return
      const current = item.perStudent[studentId]
      if (!current) return
      const previousFlag = current.score.doNotAssess
      updateLocalScore(itemKey, studentId, { doNotAssess: false })
      try {
        await updateAssessmentScoreIndividual({
          sessionMeetingId,
          assessmentScoreId: current.score.id,
          doNotAssess: false,
        })
      } catch {
        updateLocalScore(itemKey, studentId, { doNotAssess: previousFlag })
        notifications.show({
          message: tRef.current('arc_teacher_assessment_drawer.error.toggle_failed'),
          color: 'red',
          icon: <IconX size={18} />,
        })
      }
    },
    [findItem, sessionMeetingId, updateLocalScore],
  )

  const toggleStudentEnabled = useCallback(
    async (itemKey: string, studentId: string, enabled: boolean) => {
      const item = findItem(itemKey)
      if (!item) return
      const current = item.perStudent[studentId]
      if (!current) return
      if (enabled) {
        await performToggleStudentEnabledOn(itemKey, studentId)
        return
      }
      if (hasFilledScore(current.score)) {
        const studentName =
          data?.students.find(s => s.id === studentId)?.fullName ?? ''
        setDisablePrompt({ kind: 'student', itemKey, studentId, studentName })
        return
      }
      await performToggleStudentDisabled(itemKey, studentId)
    },
    [data, findItem, performToggleStudentDisabled, performToggleStudentEnabledOn],
  )

  const dismissDisablePrompt = useCallback(() => setDisablePrompt(null), [])

  const confirmDisablePrompt = useCallback(() => {
    const prompt = disablePrompt
    if (!prompt) return
    setDisablePrompt(null)
    if (prompt.kind === 'item') {
      void performToggleItemDisabled(prompt.itemKey)
    } else {
      void performToggleStudentDisabled(prompt.itemKey, prompt.studentId)
    }
  }, [disablePrompt, performToggleItemDisabled, performToggleStudentDisabled])

  return {
    loading,
    data,
    selectedStudentIds,
    visibleStudents,
    studentOptions,
    setStudentSelection,
    overwritePrompt,
    dismissOverwritePrompt,
    confirmOverwritePrompt,
    disablePrompt,
    dismissDisablePrompt,
    confirmDisablePrompt,
    setStudentScore,
    applyBulkScore,
    toggleItemEnabled,
    toggleStudentEnabled,
  }
}
